// utils/pdfGenerator.ts
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Grade, Subject } from "../types";
import { ensureRaleway } from "../src/pdf/fonts/raleway";

export interface LessonPlanInputs {
  grade: Grade;
  subject: Subject;
  chapterName: string;
}

/* ======================================================
   PDF Formatting Constants
   ====================================================== */

const BRAND_ORANGE = "#F75C03";
const BRAND_GREY = "#4D4D4D";
const TEXT_COLOR = BRAND_GREY;
const FOOTER_GREY = "#8C8C8C";

const ptToMm = (pt: number) => pt * (25.4 / 72);

const H1_SIZE = 18;
const H2_SIZE = 14;
const BODY_SIZE = 11;
const FOOTER_SIZE = 8;

const LINE_HEIGHT_FACTOR = 1.35;
const PARA_SPACING = ptToMm(6);

const MARGIN = 25;
const BULLET_INDENT = 6;        // first-level bullet indent
const NUMBERED_INDENT = 6;      // numbered list indent
const NESTED_INDENT = 12;       // nested bullet/list extra indent

const HEADER_TOP = 10;
const HEADER_H = 28;
const BODY_START_Y = HEADER_TOP + HEADER_H + 8;

/* ======================================================
   Module-level variables
   ====================================================== */

let yCursor = 0;
let pageHeight: number;
let pageWidth: number;
let doc: jsPDF;
let FONT_FAMILY: "Raleway" | "helvetica" = "helvetica";

let cachedBrandLogoDataUrl: string | null = null;
let cachedTenantLogoDataUrl: string | null = null;

async function prefetchTenantLogo(tenantLogoUrl?: string) {
  const t = String(tenantLogoUrl || "").trim();
  if (!t) return;
  try {
    if (!cachedTenantLogoDataUrl)
      cachedTenantLogoDataUrl = await loadImageAsDataUrl(t);
  } catch {
    // best-effort
  }
}

async function prefetchBrandLogo(brandLogo: string) {
  const b = String(brandLogo || "").trim();
  if (!b) return;
  try {
    if (!cachedBrandLogoDataUrl)
      cachedBrandLogoDataUrl = await loadImageAsDataUrl(b);
  } catch {
    // best-effort
  }
}

/* ======================================================
   Helpers
   ====================================================== */

function hexToRgb(hex: string) {
  const h = String(hex || "").replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function setFill(docRef: jsPDF, hex: string) {
  const { r, g, b } = hexToRgb(hex);
  docRef.setFillColor(r, g, b);
}

function setDraw(docRef: jsPDF, hex: string) {
  const { r, g, b } = hexToRgb(hex);
  docRef.setDrawColor(r, g, b);
}

function setText(docRef: jsPDF, hex: string) {
  const { r, g, b } = hexToRgb(hex);
  docRef.setTextColor(r, g, b);
}

async function loadImageAsDataUrl(urlOrDataUrl: string): Promise<string> {
  const s = String(urlOrDataUrl || "").trim();
  if (!s) throw new Error("empty_image");
  if (s.startsWith("data:image/")) return s;

  const res = await fetch(s, { mode: "cors" });
  if (!res.ok) throw new Error(`logo_fetch_failed:${res.status}`);

  const blob = await res.blob();
  return await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

function safeAddImage(
  docRef: jsPDF,
  dataUrl: string,
  x: number,
  y: number,
  w: number,
  h: number
) {
  try {
    const s = String(dataUrl || "").trim();
    if (!s.startsWith("data:image/")) return;
    const isPng = s.startsWith("data:image/png");
    const isJpg =
      s.startsWith("data:image/jpeg") || s.startsWith("data:image/jpg");
    const fmt = isPng ? "PNG" : isJpg ? "JPEG" : null;
    if (!fmt) return;
    docRef.addImage(s, fmt as any, x, y, w, h, undefined, "FAST");
  } catch {
    // best-effort
  }
}

/* ======================================================
   Header
   ====================================================== */

async function drawHeader(
  _pageNo: number,
  reportTitle: string,
  _brandLogo: string,
  _tenantLogoUrl?: string
) {
  const mx = MARGIN;
  const top = HEADER_TOP;
  const brandW = 26;
  const brandH = 14;
  const tenantW = 26;
  const tenantH = 14;

  try {
    if (cachedBrandLogoDataUrl)
      safeAddImage(doc, cachedBrandLogoDataUrl, mx, top, brandW, brandH);
  } catch { /* ignore */ }

  try {
    if (cachedTenantLogoDataUrl)
      safeAddImage(
        doc,
        cachedTenantLogoDataUrl,
        pageWidth - mx - tenantW,
        top,
        tenantW,
        tenantH
      );
  } catch { /* ignore */ }

  setText(doc, BRAND_ORANGE);
  doc.setFontSize(12);
  doc.setFont(FONT_FAMILY as any, "bold");
  doc.text(reportTitle, mx, top + brandH + 10);

  setDraw(doc, "#E6E6E6");
  doc.line(mx, top + HEADER_H, pageWidth - mx, top + HEADER_H);

  setText(doc, TEXT_COLOR);
  doc.setFontSize(BODY_SIZE);
  doc.setFont(FONT_FAMILY as any, "normal");
}

async function addNewPageWithHeader(
  pageNo: number,
  brandLogo: string,
  tenantLogoUrl: string | undefined,
  reportTitle: string
) {
  doc.addPage();
  doc.setPage(pageNo);
  await drawHeader(pageNo, reportTitle, brandLogo, tenantLogoUrl);
  yCursor = BODY_START_Y;
}

async function checkPageBreak(
  spaceNeeded: number,
  brandLogo: string,
  tenantLogoUrl: string | undefined,
  reportTitle: string,
  pageNoRef: { n: number }
) {
  if (yCursor + spaceNeeded > pageHeight - MARGIN) {
    pageNoRef.n += 1;
    await addNewPageWithHeader(pageNoRef.n, brandLogo, tenantLogoUrl, reportTitle);
  }
}

function addFooter() {
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(FOOTER_SIZE);
    doc.setFont(FONT_FAMILY as any, "normal");
    setText(doc, FOOTER_GREY);
    doc.text("© 2026 One Size Fits None™", MARGIN, pageHeight - 10);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - MARGIN, pageHeight - 10, {
      align: "right",
    });
    if (cachedTenantLogoDataUrl) {
      try {
        safeAddImage(doc, cachedTenantLogoDataUrl, MARGIN, pageHeight - 18, 10, 10);
      } catch { /* ignore */ }
    }
  }
}

/* ======================================================
   Markdown Table Parser
   Converts GFM pipe-table syntax into a 2D array
   ====================================================== */

interface ParsedTable {
  headers: string[];
  rows: string[][];
}

function parseMarkdownTable(tableLines: string[]): ParsedTable | null {
  // Filter out separator lines (e.g. |:---|:---|)
  const dataLines = tableLines.filter(
    (l) => !/^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(l)
  );
  if (dataLines.length < 1) return null;

  const parseRow = (line: string): string[] =>
    line
      .replace(/^\s*\|/, "")
      .replace(/\|\s*$/, "")
      .split("|")
      .map((cell) => cell.trim());

  const headers = parseRow(dataLines[0]);
  const rows = dataLines.slice(1).map(parseRow);

  return { headers, rows };
}

/* ======================================================
   Preprocessing — normalize to rendering tokens
   ====================================================== */

type Token =
  | { type: "h1"; text: string }
  | { type: "h2"; text: string }
  | { type: "bullet"; text: string; depth: number }
  | { type: "numbered"; text: string; n: number; depth: number }
  | { type: "table"; headers: string[]; rows: string[][] }
  | { type: "paragraph"; text: string }
  | { type: "blank" };

function tokenize(raw: string): Token[] {
  if (!raw) return [];

  // Basic cleanup
  let t = raw
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/<\/?center>/gi, "")
    .replace(/\*\*/g, "")
    .replace(/__/g, "")
    .replace(/\$([^$]+)\$/g, "$1")
    .replace(/^\s*Page\s+\d+(\s+of\s+\d+)?\s*$/gim, "")
    .replace(/^\s*©\s*\d{4}\s*One\s*Size\s*Fits\s*None.*$/gim, "")
    .replace(/\n{3,}/g, "\n\n");

  const rawLines = t.split("\n");
  const tokens: Token[] = [];
  let i = 0;

  while (i < rawLines.length) {
    const line = rawLines[i];
    const trimmed = line.trim();

    // Skip empty
    if (!trimmed) {
      tokens.push({ type: "blank" });
      i++;
      continue;
    }

    // Drop orphan hyphens
    if (/^-\s*$/.test(trimmed)) {
      i++;
      continue;
    }

    // ── Markdown table detection ──────────────────────────
    // A table block starts with a pipe-containing line, followed by a separator line
    if (trimmed.startsWith("|")) {
      // Collect consecutive pipe lines
      const tableLines: string[] = [];
      while (i < rawLines.length && rawLines[i].trim().startsWith("|")) {
        tableLines.push(rawLines[i].trim());
        i++;
      }
      const parsed = parseMarkdownTable(tableLines);
      if (parsed && parsed.headers.length > 0) {
        tokens.push({ type: "table", headers: parsed.headers, rows: parsed.rows });
      } else {
        // Fallback: render as paragraphs
        tableLines.forEach((tl) =>
          tokens.push({ type: "paragraph", text: tl.replace(/\|/g, " ").trim() })
        );
      }
      continue;
    }

    // ── H1 ───────────────────────────────────────────────
    if (trimmed === "STUDENT DIAGNOSTIC SYNOPSIS") {
      tokens.push({ type: "h1", text: trimmed });
      i++;
      continue;
    }

    // ── Markdown headings → H1 or H2 ────────────────────
    const h1Match = trimmed.match(/^#{1}\s+(.+)$/);
    if (h1Match) {
      tokens.push({ type: "h1", text: h1Match[1].trim() });
      i++;
      continue;
    }

    const h2Match = trimmed.match(/^#{2,6}\s+(.+)$/);
    if (h2Match) {
      tokens.push({ type: "h2", text: h2Match[1].trim() });
      i++;
      continue;
    }

    // ── Numbered section headings: "1. Title" at col 0 ──
    // Only treat as H2 if it's at line start with no leading spaces
    // AND the next non-empty line is NOT a numbered list item at the same depth
    const sectionMatch = line.match(/^(\d+)\.\s+(.+)$/);
    if (sectionMatch && !line.startsWith(" ") && !line.startsWith("\t")) {
      // Peek: if the next non-blank line also starts with a number+dot, it's a list, not headings
      let peek = i + 1;
      while (peek < rawLines.length && !rawLines[peek].trim()) peek++;
      const peekLine = rawLines[peek]?.trim() ?? "";
      const isListContext =
        peekLine.match(/^\d+\.\s+/) && !peekLine.match(/^#{1,6}\s/);

      if (!isListContext) {
        tokens.push({ type: "h2", text: sectionMatch[2].trim() });
        i++;
        continue;
      }
    }

    // ── Numbered lists: "  1. item" or "1. item" ─────────
    const numListMatch = line.match(/^(\s*)(\d+)\.\s+(.+)$/);
    if (numListMatch) {
      const depth = Math.floor(numListMatch[1].length / 2);
      tokens.push({
        type: "numbered",
        text: numListMatch[3].trim(),
        n: parseInt(numListMatch[2], 10),
        depth,
      });
      i++;
      continue;
    }

    // ── Bullets: "- item", "* item", "• item" ────────────
    const bulletMatch = line.match(/^(\s*)([-*•])\s+(.+)$/);
    if (bulletMatch) {
      const depth = Math.floor(bulletMatch[1].length / 2);
      tokens.push({ type: "bullet", text: bulletMatch[3].trim(), depth });
      i++;
      continue;
    }

    // ── Paragraph ────────────────────────────────────────
    tokens.push({ type: "paragraph", text: trimmed });
    i++;
  }

  return tokens;
}

/* ======================================================
   Title Page
   ====================================================== */

async function addTitlePage(
  inputs: LessonPlanInputs,
  brandLogo: string,
  tenantLogoUrl: string | undefined,
  reportTitle: string,
  pageNoRef: { n: number }
) {
  pageNoRef.n = 1;
  await drawHeader(1, reportTitle, brandLogo, tenantLogoUrl);

  yCursor = pageHeight / 3.5;

  setText(doc, BRAND_GREY);
  doc.setFontSize(24);
  doc.setFont(FONT_FAMILY as any, "bold");
  doc.text("Shape Shifter™ Lesson Plan", pageWidth / 2, yCursor, {
    align: "center",
  });

  doc.setFont(FONT_FAMILY as any, "normal");
  yCursor += 25;

  const boxWidth = 130;
  const boxX = (pageWidth - boxWidth) / 2;

  setDraw(doc, "#DDDDDD");
  setFill(doc, "#FFFFFF");
  doc.roundedRect(boxX, yCursor, boxWidth, 45, 3, 3, "FD");

  const lx = boxX + 10;
  const vx = boxX + 48;

  doc.setFontSize(BODY_SIZE);
  doc.setFont(FONT_FAMILY as any, "bold");
  setText(doc, TEXT_COLOR);
  doc.text("Chapter:", lx, yCursor + 12);
  doc.text("Grade:", lx, yCursor + 24);
  doc.text("Subject:", lx, yCursor + 36);

  doc.setFont(FONT_FAMILY as any, "normal");
  const chapterLines = doc.splitTextToSize(
    String(inputs.chapterName || ""),
    boxWidth - 58
  );
  doc.text(chapterLines, vx, yCursor + 12);
  doc.text(String(inputs.grade || ""), vx, yCursor + 24);
  doc.text(String(inputs.subject || ""), vx, yCursor + 36);
}

/* ======================================================
   Content Renderer — token-based
   ====================================================== */

async function renderContent(
  rawText: string,
  brandLogo: string,
  tenantLogoUrl: string | undefined,
  reportTitle: string,
  pageNoRef: { n: number }
) {
  const tokens = tokenize(rawText);

  pageNoRef.n += 1;
  await addNewPageWithHeader(pageNoRef.n, brandLogo, tenantLogoUrl, reportTitle);

  doc.setFontSize(BODY_SIZE);
  doc.setFont(FONT_FAMILY as any, "normal");
  setText(doc, TEXT_COLOR);
  doc.setLineHeightFactor(LINE_HEIGHT_FACTOR);

  const bodyLH = ptToMm(BODY_SIZE * LINE_HEIGHT_FACTOR);
  const h1LH = ptToMm(H1_SIZE * LINE_HEIGHT_FACTOR);
  const h2LH = ptToMm(H2_SIZE * LINE_HEIGHT_FACTOR);

  // Look-ahead helper: find next non-blank token
  const nextContent = (from: number): Token | null => {
    for (let k = from + 1; k < tokens.length; k++) {
      if (tokens[k].type !== "blank") return tokens[k];
    }
    return null;
  };

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    // ── Blank ──────────────────────────────────────────
    if (token.type === "blank") continue;

    // ── H1 ─────────────────────────────────────────────
    if (token.type === "h1") {
      const required = PARA_SPACING * 2 + h1LH + PARA_SPACING + bodyLH * 2;
      await checkPageBreak(required, brandLogo, tenantLogoUrl, reportTitle, pageNoRef);

      yCursor += PARA_SPACING * 2;
      doc.setFontSize(H1_SIZE);
      doc.setFont(FONT_FAMILY as any, "bold");
      setText(doc, BRAND_ORANGE);
      doc.text(token.text, MARGIN, yCursor);
      yCursor += h1LH + PARA_SPACING;

      setText(doc, TEXT_COLOR);
      doc.setFontSize(BODY_SIZE);
      doc.setFont(FONT_FAMILY as any, "normal");
      continue;
    }

    // ── H2 ─────────────────────────────────────────────
    if (token.type === "h2") {
      doc.setFontSize(H2_SIZE);
      doc.setFont(FONT_FAMILY as any, "bold");
      const headingLines = doc.splitTextToSize(token.text, pageWidth - MARGIN * 2);
      const headingHeight = headingLines.length * h2LH;

      const required = PARA_SPACING * 1.5 + headingHeight + PARA_SPACING + bodyLH * 2;
      await checkPageBreak(required, brandLogo, tenantLogoUrl, reportTitle, pageNoRef);

      yCursor += PARA_SPACING * 1.5;
      setText(doc, BRAND_ORANGE);
      doc.text(headingLines, MARGIN, yCursor);
      yCursor += headingHeight + PARA_SPACING;

      setText(doc, TEXT_COLOR);
      doc.setFontSize(BODY_SIZE);
      doc.setFont(FONT_FAMILY as any, "normal");
      continue;
    }

    // ── Bullet ─────────────────────────────────────────
    if (token.type === "bullet") {
      const indent = MARGIN + token.depth * NESTED_INDENT;
      const textWidth = pageWidth - indent - BULLET_INDENT - MARGIN;
      const split = doc.splitTextToSize(token.text, textWidth);
      await checkPageBreak(
        split.length * bodyLH,
        brandLogo, tenantLogoUrl, reportTitle, pageNoRef
      );

      doc.setFontSize(BODY_SIZE);
      doc.setFont(FONT_FAMILY as any, "normal");
      setText(doc, TEXT_COLOR);

      // Use a smaller bullet for nested items
      doc.text(token.depth > 0 ? "◦" : "•", indent, yCursor);
      doc.text(split, indent + BULLET_INDENT, yCursor);
      yCursor += split.length * bodyLH;

      const next = nextContent(i);
      if (!next || next.type === "bullet" || next.type === "numbered") {
        // tight list — no extra spacing
      } else {
        yCursor += PARA_SPACING;
      }
      continue;
    }

    // ── Numbered list ───────────────────────────────────
    if (token.type === "numbered") {
      const indent = MARGIN + token.depth * NESTED_INDENT;
      const numLabel = `${token.n}.`;
      const textWidth = pageWidth - indent - NUMBERED_INDENT - MARGIN;
      const split = doc.splitTextToSize(token.text, textWidth);
      await checkPageBreak(
        split.length * bodyLH,
        brandLogo, tenantLogoUrl, reportTitle, pageNoRef
      );

      doc.setFontSize(BODY_SIZE);
      doc.setFont(FONT_FAMILY as any, "normal");
      setText(doc, TEXT_COLOR);

      doc.text(numLabel, indent, yCursor);
      doc.text(split, indent + NUMBERED_INDENT, yCursor);
      yCursor += split.length * bodyLH;

      const next = nextContent(i);
      if (!next || next.type === "numbered" || next.type === "bullet") {
        // tight list
      } else {
        yCursor += PARA_SPACING;
      }
      continue;
    }

    // ── Table ───────────────────────────────────────────
    if (token.type === "table") {
      // Estimate table height to check for page break
      const rowH = bodyLH * 1.8;
      const estHeight = rowH * (token.rows.length + 1) + PARA_SPACING * 2;
      await checkPageBreak(
        Math.min(estHeight, pageHeight * 0.4), // avoid infinite loop on huge tables
        brandLogo, tenantLogoUrl, reportTitle, pageNoRef
      );

      yCursor += PARA_SPACING;

      const { r: or, g: og, b: ob } = hexToRgb(BRAND_ORANGE);
      const { r: gr, g: gg, b: gb } = hexToRgb(BRAND_GREY);
      const { r: lr, g: lg, b: lb } = hexToRgb("#F5F5F5");

      autoTable(doc, {
        startY: yCursor,
        head: [token.headers],
        body: token.rows,
        margin: { left: MARGIN, right: MARGIN },
        styles: {
          font: FONT_FAMILY as any,
          fontSize: BODY_SIZE - 1,
          textColor: [gr, gg, gb],
          cellPadding: 3,
          lineColor: [220, 220, 220],
          lineWidth: 0.3,
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [or, og, ob],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "left",
        },
        alternateRowStyles: {
          fillColor: [lr, lg, lb],
        },
        didDrawPage: async (_data) => {
          // Re-draw header on any new page autoTable creates
          pageNoRef.n = (doc as any).internal.getCurrentPageInfo().pageNumber;
          await drawHeader(pageNoRef.n, reportTitle, brandLogo, tenantLogoUrl);
        },
      });

      // Move yCursor past the table
      yCursor = (doc as any).lastAutoTable.finalY + PARA_SPACING;

      // Restore text defaults after autoTable
      doc.setFontSize(BODY_SIZE);
      doc.setFont(FONT_FAMILY as any, "normal");
      setText(doc, TEXT_COLOR);
      continue;
    }

    // ── Paragraph ──────────────────────────────────────
    if (token.type === "paragraph") {
      const split = doc.splitTextToSize(token.text, pageWidth - MARGIN * 2);
      await checkPageBreak(
        split.length * bodyLH,
        brandLogo, tenantLogoUrl, reportTitle, pageNoRef
      );

      doc.setFontSize(BODY_SIZE);
      doc.setFont(FONT_FAMILY as any, "normal");
      setText(doc, TEXT_COLOR);
      doc.text(split, MARGIN, yCursor);
      yCursor += split.length * bodyLH;

      const next = nextContent(i);
      if (
        !next ||
        next.type === "bullet" ||
        next.type === "numbered" ||
        next.type === "h1" ||
        next.type === "h2" ||
        next.type === "table"
      ) {
        yCursor += PARA_SPACING;
      }
      continue;
    }
  }
}

/* ======================================================
   Export
   ====================================================== */

export async function generateLessonPlanPdf(
  rawText: string,
  inputs: LessonPlanInputs,
  brandLogo: string,
  partnerLogo: string, // retained for compatibility; not used
  tenantLogoUrl?: string
) {
  void partnerLogo;

  cachedBrandLogoDataUrl = null;
  cachedTenantLogoDataUrl = null;

  doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  pageHeight = doc.internal.pageSize.height;
  pageWidth = doc.internal.pageSize.width;
  yCursor = BODY_START_Y;

  FONT_FAMILY = ensureRaleway(doc) ? "Raleway" : "helvetica";
  doc.setFont(FONT_FAMILY, "normal");

  await prefetchBrandLogo(brandLogo);
  await prefetchTenantLogo(tenantLogoUrl);

  const reportTitle = "Shape Shifter™ Lesson Plan";
  const pageNoRef = { n: 1 };

  await addTitlePage(inputs, brandLogo, tenantLogoUrl, reportTitle, pageNoRef);
  await renderContent(rawText, brandLogo, tenantLogoUrl, reportTitle, pageNoRef);

  addFooter();

  doc.save(
    `Shape-Shifter-Plan-${String(inputs.chapterName || "Chapter").replace(/\s/g, "_")}.pdf`
  );
}
